/**
 * SourceVisualsSection — the hand-over of the visuals a product is produced FROM,
 * rendered above the configuration it belongs to.
 *
 * Staging and floor-plan certification (the Living Area Calculation group) both
 * configure a product made from client photos and neither ever asked for them:
 * staging's own empty state sent the client BACK to the product drawer to upload,
 * which is the coupling this whole move exists to remove. Editing and floor plans
 * already collect their own, so they keep their pages and this section is not
 * rendered for them — `needsSourceUploadSurface` is the rule, and it names no
 * product.
 *
 * It sits ABOVE the module and the module is withheld until a visual arrives,
 * because the configuration is ABOUT those visuals: staging asks which rooms to
 * furnish, certification which floors to certify, and both questions are
 * unanswerable — and, in staging's case, literally unrendered — until there is
 * something to answer them about.
 *
 * `FileUploadSection` is reused verbatim rather than reimplemented: it narrows the
 * shared FileAPI scope to this product, filters out files another line owns, and
 * UNCLAIMS a filename before dropping its scope entry, so a delete is not undone
 * by `useResumedUploads` re-deriving the scope from the attachments.
 */

import React, { useEffect, useRef } from 'react'
import { Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { SystemMessage } from 'components/common/SystemMessage'
import { GRAY_700, GRAY_900 } from 'constants/styling/theme'
import type { CartItemId, ConfigurationFlowId, InputImagePolicyV2 } from 'models/purchaseFlowRevamp'

import { ProductKind, ProductKindsWithPFDocumentUpload, STAGING_PRODUCT_KINDS } from 'constants/product'
import { FileUploadSection } from '../../components/productDrawer/FileUploadSection.component'
import { DOCUMENT_SOURCE_FILE_TYPES, EDITING_FILE_TYPES } from '../../components/productDrawer/uploadFileTypes'
import { STAGING_SENTINEL_PRODUCT_ID } from '../../fileHub'
import { usePfStore } from '../../store'
import { useCartLineCatalogueEntry } from '../useCartLineCatalogueEntry'
import { useSourceVisualsClaim } from './useSourceVisualsClaim'

export interface SourceVisualsSectionProps {
  cartItemId: CartItemId
  /** The flow this page configures — what a changed visual set un-confirms. */
  flowId: ConfigurationFlowId
  /** The bought variant's policy — decides the per-visual pricing note. */
  policy: InputImagePolicyV2 | undefined
}

/**
 * @component SourceVisualsSection
 * Upload surface for a configuration whose product is produced from client visuals.
 */
export const SourceVisualsSection: React.FC<SourceVisualsSectionProps> = ({ cartItemId, flowId, policy }) => {
  const { t } = useTranslation(['purchase_flow_revamp'])
  const { scopeKey, variantId, claimedByOtherLines, claimedFilenames, isOnePerItem } =
    useSourceVisualsClaim(cartItemId, policy)
  // A document product (a floor-plan certification) takes the plan as an image or a PDF, as legacy did.
  const { shell } = useCartLineCatalogueEntry(cartItemId)
  const fileTypes = ProductKindsWithPFDocumentUpload.has(shell?.kind as ProductKind) ? DOCUMENT_SOURCE_FILE_TYPES : EDITING_FILE_TYPES

  // Mounted only for a flow that does not collect its own visuals, which is what
  // keeps ONE claim per line: editing and floor plans run theirs inside their own
  // modules, and a second hook writing the same attachments would race itself.

  // A configuration describes the visuals it was made for: staging says what to
  // do with THAT room, certification which floors THAT plan shows. Swap the file
  // and the answers no longer describe anything the client handed over — but the
  // line stayed confirmed, so a replaced photo shipped with the previous photo's
  // configuration and the step raised no objection. Changing the set re-opens it.
  //
  // Only ever un-confirms. The first value is the set the page mounted with (a
  // resumed draft, a tab switch), which is not an edit and must not un-confirm a
  // configuration the client already saved.
  const claimedKey = claimedFilenames.join(' ')
  const lastClaimedKey = useRef<string | null>(null)

  useEffect(() => {
    const previous = lastClaimedKey.current
    lastClaimedKey.current = claimedKey
    if (previous === null || previous === claimedKey) return

    usePfStore.getState().markConfigDirty(cartItemId, flowId)
  }, [claimedKey, cartItemId, flowId])

  return (
    <Stack gap={1.2} data-cy="pf-revamp-source-visuals" sx={{ mb: 2 }}>

      <Typography variant="text-lg" fontWeight={550} color={GRAY_900}>
        {t('purchase_flow_revamp:configuration.source_visuals_title')}
      </Typography>

      {/* One produced item per visual, so the number handed over is the number
          bought — said out loud, because the price follows it. */}
      {isOnePerItem &&
        <Typography variant="text-sm" color={GRAY_700}>
          {t('purchase_flow_revamp:configuration.source_visuals_one_per_item')}
        </Typography>
      }

      <FileUploadSection
        uploadProductId={STAGING_SENTINEL_PRODUCT_ID}
        productCode={variantId}
        scopeKey={scopeKey}
        claimedByOtherLines={claimedByOtherLines}
        ownerId={cartItemId}
        fileTypes={fileTypes}
        checksImageQuality={STAGING_PRODUCT_KINDS.has(shell?.kind as ProductKind)}
      />

      {/* The configuration below is withheld until a visual arrives; saying why is
          what stops an empty page reading as a broken one. */}
      {claimedFilenames.length === 0 &&
        <SystemMessage
          variant="warning"
          message={t('purchase_flow_revamp:configuration.source_visuals_required')}
        />
      }

    </Stack>
  )
}
